/**
 * UserSummaryTable - Table showing aggregated user activity summary
 */

import React from 'react';
import { UserActivitySummary } from '../types/bureau-activity.types';
import type { TodoCountEntry } from '../api/bureau-activity.api';
import {
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

interface UserSummaryTableProps {
  summary: UserActivitySummary[];
  loading: boolean;
  onUserSelect?: (userId: string) => void;
  todoCounts?: Record<string, TodoCountEntry>;
}

// Format time for display (HH:MM)
const formatTime = (time: string | null): string => {
  if (!time) return '--:--';
  return time.substring(0, 5);
};

// Get color class based on daily hours
const getHoursColor = (hours: string): string => {
  const h = parseFloat(hours);
  if (h >= 8) return 'text-green-600';
  if (h >= 6) return 'text-yellow-600';
  if (h >= 4) return 'text-orange-600';
  return 'text-red-600';
};

// Get color class based on todo count
const getTodoColor = (count: number): string => {
  if (count === 0) return 'text-green-600';
  if (count <= 5) return 'text-blue-600';
  if (count <= 20) return 'text-orange-600';
  return 'text-red-600';
};

export const UserSummaryTable: React.FC<UserSummaryTableProps> = ({
  summary,
  loading,
  onUserSelect,
  todoCounts
}) => {
  const [sortField, setSortField] = React.useState<keyof UserActivitySummary>('totalRequests');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof UserActivitySummary) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedSummary = [...summary].sort((a, b) => {
    let aVal: number | string = a[sortField] as number | string;
    let bVal: number | string = b[sortField] as number | string;

    // Handle numeric string fields
    if (typeof aVal === 'string' && !isNaN(parseFloat(aVal))) {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal as string);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const comparison = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ field }: { field: keyof UserActivitySummary }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ArrowUpIcon className="w-4 h-4 inline ml-1" />
      : <ArrowDownIcon className="w-4 h-4 inline ml-1" />;
  };

  const HeaderCell: React.FC<{ field: keyof UserActivitySummary; children: React.ReactNode; className?: string }> = ({
    field,
    children,
    className = ''
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${className}`}
      onClick={() => handleSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </th>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="animate-pulse p-4">
          <div className="h-10 bg-gray-200 rounded mb-4" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded mb-2" />
          ))}
        </div>
      </div>
    );
  }

  if (summary.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow-sm border border-gray-200">
        Aucune donnée de résumé disponible
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell field="userName">Utilisateur</HeaderCell>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">À faire</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Traités dernières 24h">24h</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Traités 7 derniers jours">7j</th>
              <HeaderCell field="daysActive" className="text-center">Jours actifs</HeaderCell>
              <HeaderCell field="totalRequests" className="text-center">Total requêtes</HeaderCell>
              <HeaderCell field="avgDailyHours" className="text-center">Heures moy./jour</HeaderCell>
              <HeaderCell field="earliestStart" className="text-center">Début le plus tôt</HeaderCell>
              <HeaderCell field="latestEnd" className="text-center">Fin la plus tardive</HeaderCell>
              <HeaderCell field="avgRequestsPerHour" className="text-center">Req/heure moy.</HeaderCell>
              <HeaderCell field="totalErrors" className="text-center">Erreurs</HeaderCell>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSummary.map((user) => (
              <tr
                key={user.userId}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onUserSelect?.(user.userId)}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {user.userName.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">{user.userName}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {todoCounts ? (
                    <span className={`text-sm font-bold ${getTodoColor(todoCounts[user.userId]?.open ?? 0)}`}>
                      {todoCounts[user.userId]?.open ?? 0}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-300">-</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {todoCounts ? (
                    <span className={`text-sm font-medium ${(todoCounts[user.userId]?.done_1d ?? 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {todoCounts[user.userId]?.done_1d ?? 0}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-300">-</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {todoCounts ? (
                    <span className={`text-sm font-medium ${(todoCounts[user.userId]?.done_7d ?? 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {todoCounts[user.userId]?.done_7d ?? 0}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-300">-</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm font-medium text-gray-900">{user.daysActive}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm font-bold text-gray-900">
                    {user.totalRequests.toLocaleString('fr-BE')}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`text-sm font-medium ${getHoursColor(user.avgDailyHours)}`}>
                    {user.avgDailyHours}h
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-600">{formatTime(user.earliestStart)}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-600">{formatTime(user.latestEnd)}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-600">{user.avgRequestsPerHour}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`text-sm font-medium ${user.totalErrors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {user.totalErrors}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
